import React, { useEffect, useRef, useState } from 'react';
import { Search, TagIcon, MapPin } from 'lucide-react';
import { listingsAPI } from '../utils/api';

/**
 * SearchAutocomplete (Iter53)
 *
 * Wraps a search input with a debounced /api/listings/suggest dropdown.
 * Renders suggestions grouped by `type`: listing | category | subcategory | combo.
 * Keyboard nav: ↑ ↓ select, Enter to confirm.
 *
 * Props:
 *  - value: controlled input value
 *  - onChange: (newValue) => void
 *  - onSubmit: (finalQuery) => void — fired on Enter or suggestion click
 *  - placeholder, className, inputClassName
 *  - testId: data-testid prefix
 */
const SearchAutocomplete = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search GenzoX',
  className = '',
  inputClassName = '',
  testId = 'search',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const q = (value || '').trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await listingsAPI.suggest(q);
        setSuggestions(Array.isArray(res.data) ? res.data : []);
        setOpen(true);
        setHighlight(-1);
      } catch (_e) { setSuggestions([]); }
    }, 180);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const confirm = (q) => {
    setOpen(false);
    onSubmit(q);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'Enter') { e.preventDefault(); confirm(value); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && suggestions[highlight]) {
        confirm(suggestions[highlight].label);
      } else {
        confirm(value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <input
        data-testid={`${testId}-input`}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute left-0 right-0 mt-1 z-40 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto"
        >
          {suggestions.map((s, i) => {
            const Icon = s.type === 'category' ? TagIcon : s.type === 'subcategory' ? TagIcon : s.type === 'combo' ? MapPin : Search;
            const subtitle =
              s.type === 'category' ? 'Category' :
              s.type === 'subcategory' ? 'Subcategory' :
              s.type === 'combo' ? `In ${s.city || ''}` :
              'Listing';
            return (
              <button
                key={`${s.type}-${s.label}-${i}`}
                type="button"
                data-testid={`${testId}-suggestion-${i}`}
                onClick={() => confirm(s.label)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-50 last:border-b-0 ${
                  highlight === i ? 'bg-[#fff4ed]' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <Icon size={14} className="flex-shrink-0 text-[#1800ad]" />
                <span className="flex-1 truncate text-[#0A0B10] font-medium">{s.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold">{subtitle}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
